import { autoGeneratedReducer } from '@hh.ru/redux-create-reducer';
import { SalaryRange } from '@hh.ru/types-hh-microcore';

import ApplicantsAvailability from 'src/constants/applicantsAvailability';
import CompetitionLevel from 'src/constants/competitionLevel';
import Currency from 'src/constants/currency';
import Education from 'src/constants/education';
import EmployeeLevel from 'src/constants/employeeLevel';
import Gender from 'src/constants/gender';
import PublicationType from 'src/constants/publicationType';
import VacancyAdvice from 'src/constants/vacancyAdvice';
import VacancyQuality from 'src/constants/vacancyQuality';
import WorkExperience from 'src/constants/workExperience';
import MarketRange from 'src/types/marketRange';

export interface Competitor {
    employerId: string;
    employerName: string;
    numberOfActiveVacancies: number;
    hasBrandedVacancies: boolean;
}

export interface Vacancy {
    vacancyId: string;
    employerId: string;
    employerName: string;
    areaId: string;
    areaName: string;
    positionName: string;
    fixedIncome: number;
    totalIncome: number;
    currencyCode: Currency;
    hasBrandedTemplate: boolean;
    publicationType: PublicationType;
}

enum IncomeType {
    Fixed = 'fixed',
    Total = 'total',
}

type IncomePercentiles = {
    income: IncomeType;
    p10: number | null;
    p25: number | null;
    p50: number | null;
    p75: number | null;
    p90: number | null;
};

type Quantity = {
    relative: number;
    absolute: number;
};

export interface CompetitorAnalysisReportData {
    competitors: Competitor[];
    similarVacancies: {
        marketRange: MarketRange;
        employersWithCompensationFrom: Quantity;
        employersWithCompensationTo: Quantity;
        brandingUsage: Quantity;
        totalCount: number;
        vacancies: Vacancy[];
    };
    marketRange: MarketRange;
    fotReport: {
        employeeLevels: Array<{
            name: EmployeeLevel;
            parts: {
                type: string;
                incomes: IncomePercentiles[];
                employersQty: number;
                positionsQty: number;
            }[];
        }>;
        isEmpty: boolean;
    };
    resumes: {
        totalNumberOfResumes: number;
        numberOfResumesWithSalary: Quantity;
        distributionBySalary: Array<{
            salaryRange: {
                min: number;
                max: number;
            };
            numberOfResumes: Quantity;
            distributionByGender: Array<{
                feature: Gender;
                value: Quantity;
            }>;
            distributionByAge: Array<{
                feature: {
                    min: number;
                    max: number;
                };
                value: Quantity;
            }>;
            distributionByWorkExperience: Array<{
                feature: WorkExperience;
                value: Quantity;
            }>;
            distributionByEducation: Array<{
                feature: Education;
                value: Quantity;
            }>;
            topKeySkills: Array<{
                feature: {
                    id: string;
                    name: string;
                };
                value: Quantity;
            }>;
            topEmployers: Array<{
                feature: {
                    id: string;
                    name: string;
                };
                value: Quantity;
            }>;
            topUniversities: Array<{
                feature: {
                    id: string;
                    name: string;
                };
                value: Quantity;
            }>;
        }>;
    };
    summary: {
        competitionLevel: CompetitionLevel;
        salaryRange: SalaryRange;
        vacancyQuality: VacancyQuality;
        applicantsAvailability: ApplicantsAvailability;
        adviceWithStatistics: Array<{ advice: VacancyAdvice }>;
    };
    axisBoundaries: {
        min: number;
        max: number;
    };
}

export default autoGeneratedReducer<Partial<CompetitorAnalysisReportData>>({});
